/**
 * mistake-similar — 為一道錯題生「再做一題類似」的題目。
 *
 * 來源策略(學習側,純讀寫學習資料):
 *   1. 數學 KP 有對應產生器 → 用 `getMathGenerator(kp)`(`MATH_GENERATORS`),
 *      最穩、離線可用、答案確定。
 *   2. 其他科目 / 沒有產生器的 KP → 用 LLM(`textComplete` 經 bridge),要求
 *      「同類型、同難度、附答案、附說明」,並以 JSON 回覆方便解析。
 *   3. LLM 不可用 / 解析失敗 → 回 `null`,呼叫端優雅降級(不顯示再練區塊)。
 *
 * 回傳的 `SimilarQuestion` 是「填答 + 即時對錯」需要的最小資料:題幹、可接受
 * 答案集合(已正規化小寫去空白)、顯示用正解、說明。MCQ 也壓平成填答(把
 * 正解選項文字當答案),維持錯題本既有「輸入框 → 比對」的純文字流。
 *
 * 鐵則:只動學習側;答對由呼叫端入帳(recordAttempt 升 box),本模組不碰進度。
 */

import { getMathGenerator } from './generators/math';
import type { GeneratedQuestion, Difficulty } from './generators/math/types';
import { textComplete } from './text-complete-client';
import { coerceJsonObject } from './llm-json';

export interface SimilarQuestion {
  /** 題幹文字。 */
  prompt: string;
  /** 顯示給小孩看的「正解」(對答後揭示)。 */
  displayAnswer: string;
  /** 一句到數句說明,怎麼算 / 為什麼。可能為空字串。 */
  explanation: string;
  /** 來源:'generator'(數學產生器)或 'llm'。供測試 / 文案參考。 */
  source: 'generator' | 'llm';
  /** 已正規化(小寫、去頭尾空白)的可接受答案集合,用於比對。 */
  acceptedAnswers: string[];
}

/** 正規化答案以利寬鬆比對(去頭尾空白 + 轉小寫;全形空白也清掉)。 */
export function normalizeAnswer(raw: string | number): string {
  return String(raw).trim().replace(/\s+/g, '').toLowerCase();
}

/**
 * 把產生器的 `GeneratedQuestion` 壓平成 `SimilarQuestion`。
 * MCQ:正解選項文字當答案;Fill:用 correctAnswer + acceptedAnswers;
 * worked-example 不拿來當練習題(回 null)。
 */
function flattenGenerated(q: GeneratedQuestion): SimilarQuestion | null {
  if (q.kind === 'mcq') {
    const answerText = String(q.options[q.correctAnswer] ?? '');
    return {
      prompt: q.prompt,
      displayAnswer: answerText,
      explanation: q.explanation,
      source: 'generator',
      acceptedAnswers: [normalizeAnswer(answerText)],
    };
  }
  if (q.kind === 'fill') {
    const accepted = [q.correctAnswer, ...(q.acceptedAnswers ?? [])].map(normalizeAnswer);
    return {
      prompt: q.prompt,
      displayAnswer: String(q.correctAnswer),
      explanation: q.explanation,
      source: 'generator',
      acceptedAnswers: Array.from(new Set(accepted)),
    };
  }
  return null; // worked-example：純講解,不當練習題。
}

/** 嘗試從 LLM 回覆字串解析出題目 JSON(容忍 fence/陣列/夾雜文字,見 llm-json)。 */
function parseLlmQuestion(text: string): SimilarQuestion | null {
  const o = coerceJsonObject(text);
  if (!o) return null;
  const prompt = typeof o.prompt === 'string' ? o.prompt.trim() : '';
  const answer =
    typeof o.answer === 'string' || typeof o.answer === 'number' ? String(o.answer).trim() : '';
  if (!prompt || !answer) return null;
  const explanation = typeof o.explanation === 'string' ? o.explanation.trim() : '';
  return {
    prompt,
    displayAnswer: answer,
    explanation,
    source: 'llm',
    acceptedAnswers: [normalizeAnswer(answer)],
  };
}

const SUBJECT_LABEL: Record<string, string> = {
  math: '數學',
  chinese: '國語',
  english: '英語',
  nature: '自然',
  social: '社會',
};

/**
 * 生一題與 `kp` 同類型的練習題。
 *
 * @param kp        知識點 id(如 `math.mul.2x1`、`chinese.zhuyin.basic`)。
 * @param subject   科目(決定 LLM 文案 / 語言)。
 * @param refPrompt 原錯題題幹,給 LLM 當「同類型」參考(產生器路徑用不到)。
 * @param seed      產生器路徑的種子(預設用時間,讓每次不同)。
 */
export async function generateSimilarQuestion(
  kp: string,
  subject: string,
  refPrompt: string,
  seed: number = Date.now() & 0x7fffffff,
): Promise<SimilarQuestion | null> {
  // 1) 數學產生器優先。
  const gen = getMathGenerator(kp);
  if (gen) {
    const difficulty: Difficulty = 2; // 與原題同難度的中段;產生器自行依 seed 變化。
    const flat = flattenGenerated(gen(seed, difficulty));
    if (flat) return flat;
  }

  // 2) LLM 路徑(其他科目 / 無產生器)。
  const subjectLabel = SUBJECT_LABEL[subject] ?? subject;
  const system =
    '你是台灣國小三年級的出題老師。只用繁體中文。輸出必須是單一 JSON 物件,不要任何多餘文字或 markdown。';
  const prompt =
    `為「${subjectLabel}」科目、知識點「${kp}」出一題與下面這題「同類型、同難度」的新練習題,` +
    `數字 / 用詞要換過,不要照抄。\n` +
    `原題:${refPrompt}\n\n` +
    `用這個 JSON 格式回覆(只回 JSON):\n` +
    `{"prompt":"新題目文字","answer":"標準答案(簡短)","explanation":"一句怎麼算或為什麼"}`;

  const raw = await textComplete({ system, prompt });
  if (!raw) return null;
  return parseLlmQuestion(raw);
}
